import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Satellite, Gauge, ShieldCheck, Globe, Radio, CreditCard, BellRing, Users, ChevronDown, ArrowRight, Check, Menu, X, Send } from "lucide-react";
import { api } from "@/lib/api";

function Counter({ target, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span ref={ref} data-testid="stat-counter">{val.toLocaleString()}{suffix}</span>;
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Landing() {
  const [stats, setStats] = useState({ operators: 180, recharges: 420000, customers: 850000 });
  const [billing, setBilling] = useState("annual");
  const [openFaq, setOpenFaq] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', mobile: '', business_name: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await api.post('/contact', contactForm);
      setContactSent(true);
      setContactForm({ name: '', email: '', mobile: '', business_name: '', message: '' });
    } catch {} finally { setContactSending(false); }
  };

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => {
      setStats({
        operators: Math.max(180, data.operators * 120 || 180),
        recharges: Math.max(420000, data.recharges * 1800 || 420000),
        customers: Math.max(850000, data.customers * 1500 || 850000),
      });
    }).catch(() => {});
  }, []);

  const features = [
    { icon: Satellite, title: "Dish TV Ready", body: "Seamlessly integrated with Dish TV operator gateway for instant recharges and subscription management." },
    { icon: ShieldCheck, title: "AES-256 Credential Vault", body: "Every recharge API key and gateway secret is encrypted at rest, scoped per admin." },
    { icon: Globe, title: "White-label Sites", body: "Auto-provision branded subdomains or bring your own CNAME — launch in minutes." },
    { icon: BellRing, title: "LCM Alert Cascade", body: "30 / 15 / 7-day expiry alerts to customer, dealer, staff and admin — SMS, email, WhatsApp." },
    { icon: CreditCard, title: "Pick-your-gateway", body: "Razorpay, Paytm, Cashfree, PhonePe — enable what your business needs." },
    { icon: Users, title: "Dealer & Staff Ledger", body: "Every customer is immutably referenced to the onboarding entity for clean commission reports." },
  ];

  const faqs = [
    { q: "Is DishLCO really zero-deploy for new LCOs?", a: "Yes — on SuperAdmin approval we auto-provision a {slug}.dishlco.com site with your brand. You can later map a custom domain." },
    { q: "Do I need to share my recharge API keys with DishLCO?", a: "Credentials are encrypted with AES-256-CBC before storage, scoped per admin. SuperAdmin only defines which providers are available." },
    { q: "Can dealers recharge on behalf of customers?", a: "Yes. Dealers and staff have dedicated portals with full recharge + collection reporting — every transaction is tagged to the reference chain." },
    { q: "What gateways are supported out of the box?", a: "Razorpay, Paytm, Cashfree and PhonePe ship as platform defaults. SuperAdmin can add more on request." },
  ];

  const plans = [
    { name: "Starter", monthly: 999, annual: 9999, subs: "Up to 500 subscribers", featured: false,
      features: ["1 white-label site", "2 payment gateways", "SMS + Email alerts", "Single recharge API"] },
    { name: "Growth", monthly: 2499, annual: 24999, subs: "Up to 5,000 subscribers", featured: true,
      features: ["Custom domain + SSL", "All payment gateways", "SMS + Email + WhatsApp", "Dealer commission ledger", "Priority support"] },
    { name: "Scale", monthly: 4999, annual: 49999, subs: "Unlimited subscribers", featured: false,
      features: ["Multi-brand sites", "Dedicated infra", "Custom recharge APIs", "SLA 99.95%", "Dedicated success manager"] },
  ];

  return (
    <div className="min-h-screen font-body text-slate-700">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
              <Satellite className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-brand-nav">DishLCO</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-brand-nav">Features</a>
            <a href="#how" className="hover:text-brand-nav">How it works</a>
            <a href="#pricing" className="hover:text-brand-nav">Pricing</a>
            <a href="#faq" className="hover:text-brand-nav">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <a href="#contact" data-testid="nav-login" className="hidden md:inline text-sm font-medium border border-brand-primary/30 text-brand-primary hover:bg-brand-primary/5 px-4 py-2 rounded-md transition-all">Request Demo</a>
            <Link to="/register" data-testid="nav-register" className="hidden md:inline text-sm font-medium bg-brand-primary hover:bg-brand-primaryHover text-white px-4 py-2 rounded-md shadow-sm transition-all">Start free →</Link>
            <button onClick={() => setMobileNav(v => !v)} className="md:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="nav-hamburger">
              {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileNav && (
          <div className="md:hidden bg-white border-t border-slate-200/60 px-6 pb-4 space-y-2">
            <a href="#features" onClick={() => setMobileNav(false)} className="block py-2 text-sm font-medium text-slate-600">Features</a>
            <a href="#how" onClick={() => setMobileNav(false)} className="block py-2 text-sm font-medium text-slate-600">How it works</a>
            <a href="#pricing" onClick={() => setMobileNav(false)} className="block py-2 text-sm font-medium text-slate-600">Pricing</a>
            <a href="#contact" onClick={() => setMobileNav(false)} className="block py-2 text-sm font-medium text-slate-600">Contact</a>
            <hr className="border-slate-200" />
            <a href="#contact" onClick={() => setMobileNav(false)} className="block py-2 text-sm font-medium border border-brand-primary/30 text-brand-primary hover:bg-brand-primary/5 rounded-md text-center transition-all mb-2">Request Demo</a>
            <Link to="/register" className="block py-2 text-sm font-medium bg-brand-primary text-white px-4 rounded-md text-center">Start free →</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(24,95,165,0.14),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(29,158,117,0.12),transparent_50%)]" />
          <img alt="" aria-hidden="true" src="https://images.unsplash.com/photo-1678156704485-59eb1592335d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBnZW9tZXRyaWMlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2OTI2MTUzfDA&ixlib=rb-4.1.0&q=85" className="absolute inset-0 w-full h-full object-cover opacity-10 animate-drift" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.18em] bg-brand-accent/10 text-brand-accent border border-brand-accent/20" data-testid="hero-tag">
              <Radio className="w-3 h-3" /> Built for DTH LCOs
            </span>
            <h1 className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-brand-nav tracking-tighter">
              Run your DTH business on <span className="hero-gradient-text">rails you actually control.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              DishLCO is a multi-tenant platform for operators, dealers and customers. White-label sites, pluggable recharge APIs, four payment gateways and a ruthless expiry-alert engine — all in one command plane.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white px-6 py-3 rounded-md font-semibold shadow-md transition-all">
                Start your white-label site <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-brand-primary/20 text-brand-primary hover:bg-brand-primary/5 px-6 py-3 rounded-md font-semibold transition-all">
                See how it works
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> No credit card</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> 7-day trial</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> Cancel anytime</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="glass-card rounded-2xl p-6 shadow-xl animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <span className="text-xs font-mono text-slate-400">skylink.dishlco.com/admin</span>
              </div>
              <div className="space-y-3">
                <div className="bg-brand-nav text-white rounded-lg p-4">
                  <div className="text-xs uppercase tracking-[0.2em] opacity-70 mb-1">Active Subscribers</div>
                  <div className="font-heading text-4xl font-bold">10,482</div>
                  <div className="text-xs text-brand-accent mt-1">↑ 12.4% this month</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500">Revenue today</div>
                    <div className="font-heading text-xl font-bold text-brand-nav">₹84,291</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="text-xs text-slate-500">Expiring 30d</div>
                    <div className="font-heading text-xl font-bold text-brand-accent">312</div>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-slate-500">Gateway share</span>
                    <span className="font-mono text-slate-400">last 7d</span>
                  </div>
                  <div className="h-2 flex rounded-full overflow-hidden bg-slate-100">
                    <div className="bg-brand-primary" style={{ width: "42%" }} />
                    <div className="bg-brand-accent" style={{ width: "28%" }} />
                    <div className="bg-amber-400" style={{ width: "18%" }} />
                    <div className="bg-slate-400" style={{ width: "12%" }} />
                  </div>
                  <div className="flex gap-3 text-[11px] mt-2 text-slate-500">
                    <span>Razorpay 42%</span><span>Paytm 28%</span><span>Cashfree 18%</span><span>PhonePe 12%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-slate-200/80 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: "Active operators",    value: stats.operators, suffix: "+" },
            { label: "Recharges processed", value: stats.recharges, suffix: "+" },
            { label: "Subscribers served",  value: stats.customers, suffix: "+" },
            { label: "Platform uptime",     value: 99, suffix: ".98%" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="text-center md:text-left">
                <div className="font-heading font-black text-4xl md:text-5xl text-brand-nav tracking-tighter" data-testid={`stat-${i}`}>
                  <Counter target={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-brand-primary font-semibold">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Everything, shipped</span>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl text-brand-nav tracking-tight">The complete operating system for DTH operators.</h2>
              <p className="mt-4 text-slate-600 text-lg">From first recharge to 30-day expiry escalation, DishLCO replaces the spreadsheets, the phone calls, and the custom portals with one clean plane of control.</p>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              const span = [0, 3].includes(i) ? "md:col-span-6" : "md:col-span-4";
              return (
                <Reveal key={i} delay={(i % 3) * 0.06} className={span}>
                  <div className="h-full bg-white border border-slate-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all">
                    <div className="w-12 h-12 rounded-xl bg-brand-accent/10 text-brand-accent flex items-center justify-center mb-5">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-nav">{f.title}</h3>
                    <p className="mt-3 text-slate-600 leading-relaxed">{f.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 md:py-32 bg-gradient-to-b from-white to-[#EEF4FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-brand-nav tracking-tight text-center max-w-2xl mx-auto">Three steps. No drama.</h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
            {[
              { n: "01", t: "Apply as an LCO", d: "Self-register and wait for SuperAdmin approval. Your white-label site is auto-provisioned the moment you're green-lit." },
              { n: "02", t: "Drop in your credentials", d: "Pick a recharge API provider and a few payment gateways from the SuperAdmin-enabled catalog. Add your own keys — AES-256 at rest." },
              { n: "03", t: "Onboard customers", d: "Dealers start creating customer accounts immediately. LCM alerts fire automatically at 30 / 15 / 7 days to expiry." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white border border-slate-200 rounded-2xl p-8 h-full hover:shadow-lg transition-all">
                  <div className="font-mono text-xs text-brand-primary font-bold">STEP {s.n}</div>
                  <h3 className="mt-3 font-heading text-2xl font-bold text-brand-nav">{s.t}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Pricing</span>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl text-brand-nav tracking-tight">Pick a plan. Extend whenever.</h2>
            </div>
          </Reveal>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex bg-slate-100 rounded-full p-1">
              {["monthly", "annual"].map((b) => (
                <button
                  key={b}
                  data-testid={`pricing-toggle-${b}`}
                  onClick={() => setBilling(b)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billing === b ? "bg-brand-primary text-white shadow-sm" : "text-slate-600 hover:text-brand-nav"}`}
                >
                  {b === "monthly" ? "Monthly" : "Annual (save 17%)"}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {plans.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className={`relative bg-white rounded-2xl p-8 h-full border-2 transition-all hover:-translate-y-1 hover:shadow-xl ${p.featured ? "border-brand-primary shadow-xl" : "border-slate-200"}`} data-testid={`pricing-card-${i}`}>
                  {p.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-primary text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">Most popular</div>
                  )}
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">{p.subs}</div>
                  <h3 className="mt-2 font-heading text-2xl font-bold text-brand-nav">{p.name}</h3>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-black text-brand-nav tracking-tighter">₹{(billing === "monthly" ? p.monthly : Math.round(p.annual / 12)).toLocaleString()}</span>
                    <span className="text-slate-500">/ mo</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">Billed {billing}{billing === "annual" ? ` (₹${p.annual.toLocaleString()}/yr)` : ""}</div>
                  <ul className="mt-6 space-y-3 text-sm text-slate-600">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2"><Check className="w-4 h-4 text-brand-accent mt-0.5 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    data-testid={`pricing-cta-${i}`}
                    className={`mt-8 block text-center px-5 py-3 rounded-md font-semibold transition-all ${p.featured ? "bg-brand-primary text-white hover:bg-brand-primaryHover" : "border border-brand-primary/20 text-brand-primary hover:bg-brand-primary/5"}`}
                  >
                    Start with {p.name}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#EEF4FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-4xl text-brand-nav tracking-tight">Operators ship faster on DishLCO.</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { img: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw3fHxpbmRpYW4lMjBtYW4lMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY5MjYxNDJ8MA&ixlib=rb-4.1.0&q=85", name: "Bikash Mohanty", role: "Dealer, Bhubaneswar", quote: "I finally know which customers are 7 days from expiring without calling anyone." },
              { img: "https://images.unsplash.com/photo-1540569014015-19a7be504e3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw4fHxpbmRpYW4lMjBtYW4lMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY5MjYxNDJ8MA&ixlib=rb-4.1.0&q=85", name: "Subrat Rout", role: "Admin, Cuttack DTH", quote: "Our own subdomain, our brand, and the recharge API I already had. Up in two hours." },
              { img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NjkyNjE0Mnww&ixlib=rb-4.1.0&q=85", name: "Smita Nayak", role: "Owner, Rourkela Cable Ops", quote: "Gateway credentials scoped per admin is the feature I didn't know I needed. Audit is now trivial." },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white border border-slate-200 rounded-2xl p-8 h-full">
                  <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
                  <div className="mt-6 flex items-center gap-4">
                    <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <div className="font-semibold text-brand-nav">{t.name}</div>
                      <div className="text-sm text-slate-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-4xl text-brand-nav tracking-tight text-center">Questions, answered.</h2>
          </Reveal>
          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-semibold text-brand-nav">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-slate-600 leading-relaxed">{f.a}</div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-nav via-brand-primary to-brand-accent p-12 md:p-16 text-white">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">Ready to replace five spreadsheets with one platform?</h2>
              <p className="mt-4 text-white/80 max-w-xl text-lg">Spin up a white-label site, hook your recharge API, and start onboarding customers — today.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="cta-register" className="bg-white text-brand-nav hover:bg-slate-100 px-6 py-3 rounded-md font-semibold transition-all">
                  Create your free admin
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / Demo Booking */}
      <section id="contact" className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Get in touch</span>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl text-brand-nav tracking-tight">Book a Demo</h2>
              <p className="mt-3 text-slate-600">Let us show you how DishLCO can transform your DTH business.</p>
            </div>
          </Reveal>
          {contactSent ? (
            <Reveal>
              <div className="mt-12 text-center bg-brand-accent/10 rounded-2xl p-8">
                <Check className="w-12 h-12 text-brand-accent mx-auto" />
                <h3 className="mt-4 font-heading font-bold text-2xl text-brand-nav">Thank you!</h3>
                <p className="mt-2 text-slate-600">We'll be in touch within 24 hours.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submitContact} className="mt-12 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input required placeholder="Your Name" value={contactForm.name} onChange={e => setContactForm(p => ({...p, name: e.target.value}))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary" />
                  <input required placeholder="Business Name" value={contactForm.business_name} onChange={e => setContactForm(p => ({...p, business_name: e.target.value}))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input required type="email" placeholder="Email" value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary" />
                  <input placeholder="Mobile" value={contactForm.mobile} onChange={e => setContactForm(p => ({...p, mobile: e.target.value}))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary" />
                </div>
                <textarea placeholder="Tell us about your business…" rows={4} value={contactForm.message} onChange={e => setContactForm(p => ({...p, message: e.target.value}))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-primary resize-none" />
                <button type="submit" disabled={contactSending}
                  className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold py-4 rounded-xl shadow-md transition-all disabled:opacity-60">
                  <Send className="w-4 h-4" /> {contactSending ? 'Sending…' : 'Request Demo'}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
              <Satellite className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-brand-nav">DishLCO</span>
            <span className="text-sm text-slate-500">© 2026</span>
          </div>
          <div className="text-sm text-slate-500 flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-brand-primary transition-colors">Privacy Policy</Link>
            <Gauge className="w-4 h-4" /> SRS v3 compliant
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 pt-4 border-t border-slate-200/60 text-center text-sm text-slate-500">
          Powered by : Oxiaim Techbreed Pvt. Ltd.
        </div>
      </footer>
    </div>
  );
}
